// @owners { team: patients-team }
import { COLORS } from '@alto/design-library-tokens';
import {
  ActionSheetContext,
  Button,
  Card,
  Link,
  LoadingEmptyState,
  SecondaryPage,
  Toast,
  ToastContext,
  XlPadding,
} from '@alto/design-system';
import { BenefitInputActionSheet } from '@alto/features';
import { useNavigation } from '@alto/navigation';
import { useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { type Resolver, useForm } from 'react-hook-form';
import { useLocation, useParams } from 'react-router-dom';
// eslint-disable-next-line import/no-deprecated
import { createInsurance, updateInsurance } from '~shared/actions/insurances';
import { setEditingInsuranceID } from '~shared/actions/onboarding';
import { getVerifyFailureErrorMessage } from '~shared/constants';
import { getInferredInsuranceFormValues } from '~shared/features/insurances/helpers/getInferredInsuranceFormValues';
import {
  type Values,
  getInitialInsuranceFormValues,
} from '~shared/features/insurances/helpers/getInitialInsuranceFormValues';
import { removeEmptyInsuranceValues } from '~shared/features/insurances/helpers/removeEmptyInsuranceValues';
import { getEditingOnboardingInsuranceID } from '~shared/features/onboarding/selectors/getOnboarding';
import getLoading from '~shared/features/ui/selectors/getLoading';
import { getCurrentUser } from '~shared/features/users/selectors/getCurrentUser';
import { getSelectedUser } from '~shared/features/users/selectors/getSelectedUser';
import { getErrorMessage } from '~shared/helpers/helper';
import { useAnalytics } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { queries } from '~shared/queries/query-keys';
import { useGetInsuranceByID } from '~shared/queries/query-keys/insurances';
import { getNotRestrictedPartnerError } from '~shared/selectors/ui/errors/getInsuranceError';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import type { Insurance } from '~shared/types';
import { InfoRequestHeader } from './InfoRequestHeader';
import { type HubReferralRouteParams } from './LandingPage';
import { NavBarLogo } from './NavBarLogo';
import { useQueryInsurancePageContent } from '~web/features/info-request/hooks';
import { useStatusBarColor } from '~web/features/info-request/hooks/useStatusBarColor';
import { InsuranceFormFields, validate } from '~web/features/onboarding/components/redesign/InsuranceFormFields';

const insuranceManualResolver: Resolver<Values> = (values) => {
  const validationErrors = validate(values);
  const errors: Record<string, { message: string; type: string }> = {};
  for (const [key, message] of Object.entries(validationErrors)) {
    if (message && typeof message === 'string') {
      errors[key] = { message, type: 'validate' };
    }
  }
  return { values, errors };
};

export const InsuranceInputManualPage = () => {
  const { navigate, goBack } = useNavigation();
  const location = useLocation();
  const { hubReferralID: hubReferralIDParam } = useParams<HubReferralRouteParams>();
  const hubReferralID = Number(hubReferralIDParam || 0);
  const dispatch = useDispatchShared();
  const loading = useSelectorShared(getLoading);
  const insuranceID = useSelectorShared(getEditingOnboardingInsuranceID);
  const { isFetching, headerLogoUrl, redirectURL } = useQueryInsurancePageContent({
    hubReferralID,
  });
  const queryClient = useQueryClient();
  const { trackEvent, trackPageView } = useAnalytics();
  const { addToast } = useContext(ToastContext);
  const { setActiveActionSheet } = useContext(ActionSheetContext);

  const selectedUser = useSelectorShared(getSelectedUser);
  const currentUser = useSelectorShared(getCurrentUser);
  const user = selectedUser || currentUser;
  const { insurance: savedOnboardingInsurance } = useGetInsuranceByID(insuranceID);
  const currentPath = location.pathname;

  const nextURL = currentPath.replace('/insurance-input-manual/', '/insurance/');

  const error = useSelectorShared(getNotRestrictedPartnerError);
  const saving = loading.createInsuranceLoading || loading.updateInsuranceLoading;

  const openInsuranceInfoActionSheet = () => {
    setActiveActionSheet(<BenefitInputActionSheet />);
  };

  const handleGoBack = useCallback(() => {
    goBack();
  }, [goBack]);

  const defaultValues = useMemo(
    () => getInitialInsuranceFormValues(savedOnboardingInsurance),
    [savedOnboardingInsurance],
  );

  const {
    control,
    handleSubmit,
    formState: { isSubmitted },
    reset,
  } = useForm<Values>({
    defaultValues,
    resolver: insuranceManualResolver,
    mode: 'onSubmit',
  });

  useEffect(() => {
    reset(getInitialInsuranceFormValues(savedOnboardingInsurance));
  }, [defaultValues, reset, savedOnboardingInsurance]);

  const afterSave = useCallback(
    (newInsurance: Insurance) => {
      if (!newInsurance) {
        addToast(<Toast variant="error">{getErrorMessage(error) || getVerifyFailureErrorMessage()}</Toast>);
        trackEvent({
          event: EVENTS.INSURANCE_ADD_FAILED,
          params: {
            type: 'manual',
            source: 'hub-referral-info-request',
            next_url: nextURL,
          },
        });
        return;
      }
      queryClient.invalidateQueries({ queryKey: queries.insurances.fetchAll._def });
      dispatch(setEditingInsuranceID(null));

      if (!savedOnboardingInsurance) {
        trackEvent({
          event: EVENTS.INSURANCE_ADDED,
          params: {
            type: 'manual',
            source: 'hub-referral-info-request',
            entity_name: newInsurance.insurance_plan_name,
            next_url: nextURL,
          },
        });
      } else {
        queryClient.invalidateQueries({
          queryKey: queries.insurances.fetchOne({ insuranceID: savedOnboardingInsurance.id }).queryKey,
        });
      }

      navigate(nextURL);
    },
    [addToast, error, trackEvent, nextURL, queryClient, dispatch, savedOnboardingInsurance, navigate],
  );

  const onSubmit = useCallback(
    async (formValues: Values) => {
      trackEvent({
        event: EVENTS.INFO_REQUEST__SAVE_INSURANCE_CLICKED,
        params: {
          hub_referral_id: hubReferralID,
          insurance_input_type: 'manual',
          patient_id: user?.id,
          next_url: nextURL,
        },
      });
      if (user) {
        const newInsuranceValues = getInferredInsuranceFormValues(formValues, user);
        if (savedOnboardingInsurance) {
          return dispatch(
            // eslint-disable-next-line import/no-deprecated
            updateInsurance({
              id: savedOnboardingInsurance.id,
              ...removeEmptyInsuranceValues(newInsuranceValues),
            }),
          ).then(afterSave);
        } else {
          return dispatch(
            // eslint-disable-next-line import/no-deprecated
            createInsurance({
              user_id: user.id,
              hub_referral_id: hubReferralID,
              ...removeEmptyInsuranceValues(newInsuranceValues),
            }),
          ).then(afterSave);
        }
      }
    },
    [hubReferralID, nextURL, user, savedOnboardingInsurance, dispatch, trackEvent, afterSave],
  );

  const onSave = handleSubmit(onSubmit);

  const onCancel = useCallback(() => {
    dispatch(setEditingInsuranceID(null));
    handleGoBack();
  }, [dispatch, handleGoBack]);

  useStatusBarColor(COLORS.BACKGROUND_COLORS.GREY_LIGHTEST);

  useEffect(() => {
    trackPageView({
      event: EVENTS.INFO_REQUEST__INSURANCE_INPUT_MANUAL_PAGE_VIEWED,
      params: { hubReferralID },
    });
  }, [trackPageView, hubReferralID]);

  useEffect(() => {
    if (redirectURL) {
      navigate(redirectURL);
    }
  }, [navigate, redirectURL]);

  if (isFetching) {
    return (
      <XlPadding>
        <LoadingEmptyState />
      </XlPadding>
    );
  }

  return (
    <SecondaryPage
      headerBackgroundColor={COLORS.BACKGROUND_COLORS.GREY_LIGHTEST}
      withoutWebFramingElementHeights
      dismissIcon="chevronleft"
      onDismiss={handleGoBack}
      NavBarCenterContent={headerLogoUrl ? <NavBarLogo illustrationSrc={headerLogoUrl} /> : undefined}
      HeaderContent={
        <InfoRequestHeader
          title="Add insurance details"
          link={
            <Link
              onPress={openInsuranceInfoActionSheet}
              center
            >
              Where can I find this?
            </Link>
          }
        />
      }
      buttons={[
        <Button
          label="Cancel"
          onPress={onCancel}
          type="tertiary"
          key="cancel button"
          loading={saving}
        />,
        <Button
          label="Save insurance"
          onPress={onSave}
          loading={saving}
          key="submit button"
        />,
      ]}
      footerPlacementContext="webScreenWithoutPolicyFooter"
    >
      <Card>
        <InsuranceFormFields
          control={control}
          isSubmitted={isSubmitted}
        />
      </Card>
    </SecondaryPage>
  );
};
